import React, {
  useCallback,
  useContext,
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js'

import {
  NewCardInput,
  OnlineOrderingSpiCreatePaymentIntentSuccessResponse,
  OnlineOrderingSpiUpdatePaymentIntentSuccessResponse,
  PlaceOrderErrorCode,
  useCreateGiftCardPaymentIntentMutation,
  useCreatePaymentIntentMutation,
  useUpdateGiftCardPaymentIntentMutation,
  useUpdatePaymentIntentMutation,
  PaymentType
} from 'src/apollo/onlineOrdering'
import { reportErrorMessageWithData } from 'src/lib/js/clientError'
import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'

import { useFlags } from 'shared/components/common/feature_flags/useFlags'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { useSift } from 'public/components/default_template/meta/sift/SiftContext'
import {
  useServerSpi,
  useSpi,
  useSpiSdk,
  useSpiEventTracker
} from 'public/components/default_template/online_ordering/checkout/payment/useSpi'
import {
  useCart,
  getOrderSource
} from 'public/components/online_ordering/CartContext'
import { CheckoutFormData } from 'public/components/online_ordering/CheckoutContext'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import {
  ReCaptchaFailure,
  useGenerateReCaptchaTokenForPayment
} from 'public/components/online_ordering/recaptcha'
import { useApplePay } from 'public/components/online_ordering/applePayUtils'
import {
  BillingDetails,
  PaymentIntent,
  CreatePaymentMethodResultEvent,
  CreatePaymentMethodResultEventContent,
  ToastPaymentType,
  CardDetails
} from 'public/components/online_ordering/types'

import { resources, giftCardRecaptchaSiteKey } from 'config'

export type PaymentCompletedParams = {
  paymentIntentId?: string | null
  paymentMethodId?: string | null
  surchargeAmount?: number | null
  sessionSecret?: string | null
}

type PaymentError = {
  message: string
  type: string
}

export enum PaymentOption {
  UponReceipt = 'UponReceipt',
  PayNow = 'PayNow'
}

/**
 * Why the payment intent couldn't be created. Drives what the guest is told, so
 * each case is one they can act on differently.
 */
export enum PaymentIntentFailureReason {
  /** reCAPTCHA never loaded, so the intent had no token to be validated with. */
  ReCaptchaBlocked = 'RECAPTCHA_BLOCKED',
  /** reCAPTCHA loaded but couldn't verify the guest in time. */
  ReCaptchaUnverified = 'RECAPTCHA_UNVERIFIED',
  /** Anything else — a network blip, a backend error. */
  Unknown = 'UNKNOWN'
}

export type PaymentIntentFailure = {
  reason: PaymentIntentFailureReason
  /** False once the guest has used their retry, or when retrying can't help. */
  canRetry: boolean
}

/**
 * A blocked script won't unblock itself, so retrying only wastes the guest's
 * time — every other reason gets one attempt.
 */
const RETRYABLE_REASONS = [
  PaymentIntentFailureReason.ReCaptchaUnverified,
  PaymentIntentFailureReason.Unknown
]
const MAX_MANUAL_INTENT_RETRIES = 1

const REASONS_BY_RECAPTCHA_FAILURE: Record<
  ReCaptchaFailure,
  PaymentIntentFailureReason
> = {
  [ReCaptchaFailure.Blocked]: PaymentIntentFailureReason.ReCaptchaBlocked,
  [ReCaptchaFailure.Unverified]: PaymentIntentFailureReason.ReCaptchaUnverified
}

export type SelectedCreditCard = {
  newCardSelected: boolean
  savedCardGuid: string | null
}

export type PaymentContextType = {
  createPaymentIntent: () => Promise<void>
  paymentIntent?: OnlineOrderingSpiCreatePaymentIntentSuccessResponse
  /** Set when the current intent creation failed; null while one is in flight. */
  paymentIntentFailure: PaymentIntentFailure | null
  retryPaymentIntent: () => Promise<void>
  setPaymentMethod: (
    paymentMethod: CreatePaymentMethodResultEventContent | undefined
  ) => void
  createPaymentMethod: (
    giftCardAppliedAmount: number,
    toastCashAppliedAmount: number,
    onSuccess: (ev: CreatePaymentMethodResultEvent) => void | Promise<void>,
    onFailure: any,
    onCancel?: any
  ) => Promise<void>
  completePayment: (
    checkoutFormData: CheckoutFormData,
    giftCardAppliedAmount: number,
    toastCashAppliedAmount: number,
    validate: (() => Promise<boolean>) | null,
    onComplete: (
      paymentId?: string,
      firstName?: string,
      lastName?: string,
      phone?: string,
      email?: string,
      phoneCountryCode?: string,
      paymentCompletedParams?: PaymentCompletedParams
    ) => Promise<any>,
    onCancel: (
      needInput: boolean,
      firstName?: string,
      lastName?: string,
      phone?: string,
      email?: string
    ) => void,
    onFail: (err: PaymentError | null) => void
  ) => Promise<void>
  completeGiftCardPayment: (
    data: any,
    onComplete: (paymentId: string) => Promise<any>,
    onCancel: () => void,
    onFail: (err: PaymentError | null) => void
  ) => Promise<void>
  confirmedPayment: PaymentIntent | null
  billingDetails?: BillingDetails
  userInfoRequired: boolean
  setUserInfoRequired: (userInfoRequired: boolean) => void
  isPaymentValid: boolean
  setIsPaymentValid: (valid: boolean) => void
  // the paymentType that was selected via SPI
  paymentType: string | null
  setPaymentType: (paymentType: ToastPaymentType) => void
  paymentOption: PaymentOption | null
  setPaymentOption: (option: PaymentOption | null) => void
  newCreditCard: NewCardInput | null
  setNewCreditCard: (card: NewCardInput | null) => void
  setSaveNewCreditCard: (saveCard: boolean) => void
  selectedCreditCard: SelectedCreditCard | null
  setSelectedCreditCard: (
    card:
      | SelectedCreditCard
      | null
      | ((old: SelectedCreditCard) => SelectedCreditCard)
  ) => void
  tipEnabled: boolean
  tipAmount: number
  setTipAmount: (tip: number) => void
  tipPercent: number | undefined
  setTipPercent: (percent: number | undefined) => void
  fundraisingEnabled: boolean
  fundraisingAmount: number
  setFundraisingAmount: (amount: number) => void
  toastCashInfo: ToastCashInfo | null
  setToastCashInfo: (info: ToastCashInfo | null) => void
  cardDetails?: CardDetails
  giftCardPaymentIntentError: boolean
}

const TIPPABLE_PAYMENT_OPTIONS = [PaymentOption.PayNow] as PaymentOption[]

export type ToastCashInfo = {
  toastCashAvailableAmount: number
  toastCashAccountId: string
}

export const calculateFundraisingEnabled = (
  fundraisingConfig:
    | { active?: boolean | null; donationType?: string | null }
    | null
    | undefined,
  paymentOption: PaymentOption | null,
  surcharges: any,
  featureFlagEnabled?: boolean
): boolean => {
  const isFundraisingActive = Boolean(fundraisingConfig?.active)
  const isPayNow = paymentOption === PaymentOption.PayNow

  if (!isFundraisingActive || !isPayNow) {
    return false
  }

  const isRoundUpCampaign = fundraisingConfig?.donationType === 'ROUND_UP'
  const hasSurcharges = surcharges && Object.keys(surcharges).length > 0
  if (isRoundUpCampaign && hasSurcharges) {
    // Support round up campaigns with surcharging if the feature flag is enabled
    return Boolean(featureFlagEnabled)
  } else {
    return true
  }
}

export const PaymentContext = createContext<PaymentContextType | undefined>(
  undefined
)

export const getCheckoutInfo = (
  checkoutFormData: CheckoutFormData,
  billingDetails?: BillingDetails | null
) => {
  // The SPI SDK returns the user's name as a single string (partly because the GooglePay SDK does), so try to parse the name here
  const firstName =
    checkoutFormData.yourInfoFirstName ||
    (billingDetails?.name || '').split(' ')?.[0]
  const lastName =
    checkoutFormData.yourInfoLastName ||
    (billingDetails?.name || '').split(' ')?.[1]
  const phoneNumber =
    checkoutFormData.yourInfoPhone || billingDetails?.phoneNumber
  const email = checkoutFormData.yourInfoEmail || billingDetails?.email
  let phoneCountryCode

  if (
    checkoutFormData.yourInfoPhone &&
    isValidPhoneNumber(checkoutFormData.yourInfoPhone)
  ) {
    phoneCountryCode = parsePhoneNumber(
      checkoutFormData.yourInfoPhone
    ).countryCallingCode
  } else if (billingDetails?.phoneNumber) {
    // Default to 1 since billingDetails is returned from the SPI SDK, which is only available in the US
    phoneCountryCode = '1'
  }

  return { firstName, lastName, phoneNumber, email, phoneCountryCode }
}

export const PaymentContextProvider = (
  props: React.PropsWithChildren<{
    useGiftCardFlow?: boolean
    giftCardAmount?: number
    skipPaymentIntent?: boolean
  }>
) => {
  const { skipPaymentIntent, useGiftCardFlow, giftCardAmount } = props
  const ooClient = useOOClient()
  const { sessionId } = useSift(skipPaymentIntent)
  const { spiSurchargingEnabled } = useSpi()
  const { serverSpiEnabled } = useServerSpi()
  const isIntlRestaurant = useIsIntlRestaurant()
  const spiSdk = useSpiSdk(skipPaymentIntent)
  const { trackSpi } = useSpiEventTracker()
  const { ooRestaurant, selectedLocation, toastProduct } = useRestaurant()
  const { cart, cartGuid, surcharges } = useCart()
  const { customer } = useCustomer()
  const { dotFundraisingSurcharging } = useFlags()
  const generateReCaptchaTokenForPayment = useGenerateReCaptchaTokenForPayment()
  const [createPaymentIntentMutation] = useCreatePaymentIntentMutation({
    client: ooClient
  })
  const [updatePaymentIntentMutation] = useUpdatePaymentIntentMutation({
    client: ooClient
  })
  const [createGiftCardPaymentIntentMutation] =
    useCreateGiftCardPaymentIntentMutation({ client: ooClient })
  const [updateGiftCardPaymentIntentMutation] =
    useUpdateGiftCardPaymentIntentMutation({ client: ooClient })
  const { getConfig } = useApplePay()

  const [paymentIntent, setPaymentIntent] = useState<
    OnlineOrderingSpiCreatePaymentIntentSuccessResponse | undefined
  >()
  const [paymentOption, setPaymentOption] = useState<PaymentOption | null>(null)
  const [paymentType, setPaymentType] = useState<ToastPaymentType | null>(null)
  const [newCreditCard, setNewCreditCard] = useState<NewCardInput | null>(null)
  const [isPaymentValid, setIsPaymentValid] = useState(false)
  const [giftCardPaymentIntentError, setGiftCardPaymentIntentError] =
    useState(false)
  const [selectedCreditCard, setSelectedCreditCard] =
    useState<SelectedCreditCard | null>(null)
  const [tipAmount, setTipAmount] = useState(0.0)
  const [tipPercent, setTipPercent] = useState<number | undefined>(0) // undefined can indicate a custom tip
  const tipEnabled = useMemo(() => {
    return Boolean(
      ooRestaurant?.creditCardConfig.tipEnabled &&
        paymentOption &&
        TIPPABLE_PAYMENT_OPTIONS.includes(paymentOption)
    )
  }, [ooRestaurant, paymentOption])
  const [fundraisingAmount, setFundraisingAmount] = useState(0.0)
  const fundraisingEnabled = useMemo(() => {
    return calculateFundraisingEnabled(
      ooRestaurant?.fundraisingConfig,
      paymentOption,
      surcharges,
      dotFundraisingSurcharging
    )
  }, [
    ooRestaurant?.fundraisingConfig,
    paymentOption,
    surcharges,
    dotFundraisingSurcharging
  ])
  const [userInfoRequired, setUserInfoRequired] = useState(true)
  const [confirmPaymentError, setConfirmPaymentError] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<
    CreatePaymentMethodResultEventContent | undefined
  >(undefined)
  const [confirmedPayment, setConfirmedPayment] = useState(null)
  const [cardDetails, setCardDetails] = useState<CardDetails | undefined>()

  useEffect(() => {
    // If the value of the precomputed tips change due to the value of the order changing,
    // and the guest has selected a tip percent, make sure the value of the tip is updated.
    if (tipPercent && cart?.preComputedTips) {
      const newTip = cart.preComputedTips.find(
        (tip) => tip?.percent === tipPercent
      )
      if (newTip) {
        setTipAmount(newTip.value)
      }
    }
  }, [cart?.preComputedTips, tipPercent])

  // changing the payment type should cause a new payment method to be created when checking out
  const updatePaymentType = useCallback((newPaymentType: ToastPaymentType) => {
    setPaymentType(newPaymentType)
    setPaymentMethod(undefined)
  }, [])
  const [toastCashInfo, setToastCashInfo] =
    React.useState<ToastCashInfo | null>(null)

  useEffect(() => {
    if (cart) {
      const canPayAtCheckout = cart.paymentOptions.atCheckout.length > 0
      const canPayUponReceipt = cart.paymentOptions.uponReceipt.length > 0
      if (!canPayAtCheckout && !canPayUponReceipt) {
        setPaymentOption(null)
      } else {
        if (
          canPayAtCheckout &&
          (paymentOption === null ||
            (paymentOption === PaymentOption.UponReceipt && !canPayUponReceipt))
        ) {
          setPaymentOption(PaymentOption.PayNow)
        } else if (
          canPayUponReceipt &&
          (paymentOption === null ||
            (paymentOption === PaymentOption.PayNow && !canPayAtCheckout))
        ) {
          setPaymentOption(PaymentOption.UponReceipt)
        }
      }
    }
  }, [cart, paymentOption])

  // Guards against overlapping createPaymentIntent calls resolving out of order.
  // Without this, a slow earlier request can overwrite the intent from a later
  // one, leaving the SPI session secret pointing at a superseded intent.
  const createPaymentIntentSeq = useRef(0)

  const [paymentIntentFailure, setPaymentIntentFailure] =
    useState<PaymentIntentFailure | null>(null)
  const manualIntentRetries = useRef(0)

  // The reCAPTCHA outcome is what makes the difference between "your browser is
  // blocking our security check" and a generic failure, so the caller passes it
  // in rather than us re-deriving it from the mutation's error message.
  const failPaymentIntent = useCallback(
    (reCaptchaFailure?: ReCaptchaFailure) => {
      const reason = reCaptchaFailure
        ? REASONS_BY_RECAPTCHA_FAILURE[reCaptchaFailure]
        : PaymentIntentFailureReason.Unknown

      setPaymentIntentFailure({
        reason,
        canRetry:
          RETRYABLE_REASONS.includes(reason) &&
          manualIntentRetries.current < MAX_MANUAL_INTENT_RETRIES
      })
    },
    []
  )

  const createPaymentIntent = useCallback(async () => {
    // Work out whether we'll actually dispatch before claiming a sequence
    // number. A call that falls through both branches (giftCardAmount of 0 or
    // NaN, or no cartGuid yet) must not invalidate a request already in flight,
    // or that request's response is dropped and nothing retries it.
    const isGiftCardIntent = Boolean(useGiftCardFlow && giftCardAmount)
    const isOoIntent =
      !isGiftCardIntent && !isIntlRestaurant && Boolean(cartGuid)
    if (!isGiftCardIntent && !isOoIntent) {
      return
    }

    const seq = ++createPaymentIntentSeq.current
    const isStale = () => seq !== createPaymentIntentSeq.current
    setPaymentIntentFailure(null)

    // The branch conditions repeat the checks above rather than reusing the
    // booleans: the mutation inputs require non-null giftCardAmount/cartGuid,
    // and only the inline checks narrow those types.
    if (useGiftCardFlow && giftCardAmount) {
      try {
        const reCaptchaToken = await new Promise<string>((resolve, reject) => {
          grecaptcha.enterprise.ready(async () => {
            try {
              const token = await grecaptcha.enterprise.execute(
                giftCardRecaptchaSiteKey,
                { action: 'EGiftCard_CreatePaymentIntent' }
              )
              resolve(token)
            } catch (e) {
              reject(e)
            }
          })
        })

        const res = await createGiftCardPaymentIntentMutation({
          variables: {
            input: {
              amount: giftCardAmount,
              restaurantGuid: selectedLocation.externalId,
              orderSource: getOrderSource(toastProduct),
              deliveryProvider: cart?.deliveryProviderInfo?.provider,
              sessionId,
              reCaptchaToken
            }
          }
        })
        if (isStale()) {
          return
        }
        if (
          res?.data?.oo?.spiCreateGiftCardPaymentIntent?.__typename ===
          'OnlineOrderingSpiCreatePaymentIntentSuccessResponse'
        ) {
          setPaymentIntent(res.data.oo.spiCreateGiftCardPaymentIntent)
        } else if (
          res?.data?.oo?.spiCreateGiftCardPaymentIntent?.__typename ===
          'OnlineOrderingPaymentIntentError'
        ) {
          reportErrorMessageWithData('Error creating payment intent', {
            message: res.data.oo.spiCreateGiftCardPaymentIntent.message
          })
          setGiftCardPaymentIntentError(true)
        }
      } catch (e) {
        if (isStale()) {
          return
        }
        reportErrorMessageWithData('Error creating payment intent', {
          message: e.message
        })
        setGiftCardPaymentIntentError(true)
      }
    } else if (!isIntlRestaurant && cartGuid) {
      const { token: reCaptchaToken, failure: reCaptchaFailure } =
        await generateReCaptchaTokenForPayment()

      try {
        const res = await createPaymentIntentMutation({
          variables: {
            input: {
              cartGuid,
              paymentMethodConfigId: resources.paymentMethodConfigId,
              orderSource: getOrderSource(toastProduct),
              deliveryProvider: cart?.deliveryProviderInfo?.provider,
              sessionId,
              reCaptchaToken
            }
          }
        })
        if (
          res?.data?.oo?.spiCreatePaymentIntent?.__typename ===
          'OnlineOrderingSpiCreatePaymentIntentSuccessResponse'
        ) {
          // The backend did create this intent, so report it either way:
          // superseded creations are the signal for how often the intent is
          // being replaced, which is what tells us whether the desync is closed.
          if (isStale()) {
            trackSpi('temp_SPI_payment_intent_discarded', {
              paymentId: res.data.oo.spiCreatePaymentIntent.id
            })
            return
          }
          setPaymentIntent(res.data.oo.spiCreatePaymentIntent)
          setConfirmedPayment(null)
          manualIntentRetries.current = 0
          setPaymentIntentFailure(null)
          trackSpi('temp_SPI_payment_intent_created', {
            paymentId: res.data.oo.spiCreatePaymentIntent.id
          })
        } else if (
          res?.data?.oo?.spiCreatePaymentIntent?.__typename ===
          'OnlineOrderingPaymentIntentError'
        ) {
          const stale = isStale()
          if (!stale) {
            failPaymentIntent(reCaptchaFailure)
            reportErrorMessageWithData('Error creating payment intent', {
              message: res.data.oo.spiCreatePaymentIntent.message
            })
          }
          trackSpi('temp_SPI_payment_intent_create_failed', {
            type: 'OnlineOrderingPaymentIntentError',
            message: res.data.oo.spiCreatePaymentIntent.message,
            superseded: stale
          })
        }
      } catch (e) {
        const stale = isStale()
        if (!stale) {
          failPaymentIntent(reCaptchaFailure)
          reportErrorMessageWithData('Error creating payment intent', {
            message: e.message
          })
        }
        trackSpi('temp_SPI_payment_intent_create_failed', {
          type: 'UnknownException',
          message: e.message,
          superseded: stale
        })
      }
    }
  }, [
    trackSpi,
    createPaymentIntentMutation,
    useGiftCardFlow,
    giftCardAmount,
    createGiftCardPaymentIntentMutation,
    generateReCaptchaTokenForPayment,
    failPaymentIntent,
    selectedLocation,
    cartGuid,
    isIntlRestaurant,
    toastProduct,
    cart?.deliveryProviderInfo?.provider,
    sessionId
  ])

  // The guest's one manual attempt. Clearing the failure first puts the payment
  // form back into its loading state, so the retry reads as progress.
  const retryPaymentIntent = useCallback(() => {
    manualIntentRetries.current += 1
    setPaymentIntentFailure(null)
    return createPaymentIntent()
  }, [createPaymentIntent])

  // re-create the payment intent if the user logged in or out
  // TODO: move this closer to where the SPI SDK is loaded? It's possible that the PaymentContext is used outside of SPI.
  // Keyed on the customer's id rather than the customer object: the object identity
  // changes on any refetch or auth token refresh, which would recreate the intent
  // (and invalidate the SPI session secret) without the guest having logged in or out.
  const customerId = customer?.id ?? null
  useEffect(() => {
    if (!isIntlRestaurant && !skipPaymentIntent) {
      createPaymentIntent()
    }
  }, [
    isIntlRestaurant,
    skipPaymentIntent,
    cartGuid,
    createPaymentIntent,
    customerId
  ])

  /**
   * Updates the payment intent ahead of confirming it client-side.
   * Only used when server-side SPI is disabled; with server-side SPI the
   * server updates and confirms the intent while placing the order.
   */
  const updatePaymentIntent = useCallback(
    async (
      email: string,
      restaurantGiftCardPaymentAmount: number,
      globalGiftCardPaymentAmount: number,
      toastCashPaymentAmount: number,
      paymentMethodId?: string
    ): Promise<OnlineOrderingSpiUpdatePaymentIntentSuccessResponse | void> => {
      if (
        cartGuid &&
        email &&
        paymentIntent &&
        paymentOption === PaymentOption.PayNow
      ) {
        const surcharge = spiSurchargingEnabled
          ? surcharges?.[PaymentType.Credit]?.surchargeAmount ?? null
          : null
        const totalTaxAmount = cart?.order?.taxV2 ?? null
        const response = await updatePaymentIntentMutation({
          variables: {
            input: {
              cartGuid,
              email,
              globalGiftCardPaymentAmount:
                globalGiftCardPaymentAmount + toastCashPaymentAmount,
              paymentIntentId: paymentIntent.id,
              restaurantGiftCardPaymentAmount,
              tipAmount,
              fundraisingAmount,
              enableConfirmRetry: confirmPaymentError,
              paymentMethodId,
              amountDetails: {
                tip: Math.round(tipAmount * 100),
                ...(surcharge !== null && {
                  surcharge: Math.round(surcharge * 100)
                }),
                ...(totalTaxAmount !== null && {
                  tax: {
                    totalTaxAmount: Math.round(totalTaxAmount * 100)
                  }
                })
              }
            }
          }
        })
        if (
          response.data?.oo.spiUpdatePaymentIntent.__typename ===
          'OnlineOrderingPaymentIntentError'
        ) {
          trackSpi('temp_SPI_payment_intent_update_failed', {
            paymentIntentId: paymentIntent.id
          })
          reportErrorMessageWithData(
            'Error placing order - failed to update payment intent',
            { paymentIntentId: paymentIntent?.id }
          )
          throw {
            type: PlaceOrderErrorCode.PlaceOrderFailed,
            message: response.data.oo.spiUpdatePaymentIntent.message
          }
        } else {
          trackSpi('temp_SPI_payment_intent_updated', {})
        }

        if (
          response.data?.oo.spiUpdatePaymentIntent.__typename ===
          'OnlineOrderingSpiUpdatePaymentIntentSuccessResponse'
        ) {
          return response.data?.oo
            .spiUpdatePaymentIntent as OnlineOrderingSpiUpdatePaymentIntentSuccessResponse
        }
      }
    },
    [
      trackSpi,
      cartGuid,
      cart,
      paymentIntent,
      paymentOption,
      tipAmount,
      fundraisingAmount,
      updatePaymentIntentMutation,
      confirmPaymentError,
      spiSurchargingEnabled,
      surcharges
    ]
  )

  const updateGiftCardPaymentIntent = useCallback(
    async (email: string, globalGiftCardPaymentAmount: number) => {
      if (giftCardAmount && email && paymentIntent) {
        const response = await updateGiftCardPaymentIntentMutation({
          variables: {
            input: {
              restaurantGuid: selectedLocation.externalId,
              email,
              globalGiftCardPaymentAmount,
              paymentIntentId: paymentIntent.id,
              giftCardAmount,
              enableConfirmRetry: confirmPaymentError
            }
          }
        })
        if (
          response.data?.oo.spiUpdateGiftCardPaymentIntent.__typename ===
          'OnlineOrderingPaymentIntentError'
        ) {
          throw {
            type: PlaceOrderErrorCode.PlaceOrderFailed,
            message: response.data.oo.spiUpdateGiftCardPaymentIntent.message
          }
        }
      }
    },
    [
      selectedLocation,
      giftCardAmount,
      paymentIntent,
      updateGiftCardPaymentIntentMutation,
      confirmPaymentError
    ]
  )

  const spiConfirmPaymentFailureCallback = useCallback(
    (onFailed: any) => (ev: any) => {
      trackSpi('temp_SPI_confirmPayment_failed', {
        message: ev?.cause?.message,
        paymentIntentId: paymentIntent?.id
      })
      setConfirmPaymentError(true)
      reportErrorMessageWithData(
        'Error placing order - failed to confirm payment',
        {
          message: ev?.cause?.message,
          error: ev,
          paymentIntentId: paymentIntent?.id
        }
      )
      onFailed({
        type: PlaceOrderErrorCode.PlaceOrderFailed,
        message: 'We were unable to place your order.'
      })
      spiSdk.clearPaymentSelection()
    },
    [trackSpi, spiSdk, paymentIntent]
  )

  const spiPaymentMethodSuccessCallback = useCallback(
    (
        checkoutFormData: any,
        giftCardAppliedAmount: number | null,
        toastCashAppliedAmount: number | null,
        validate: any,
        onConfirmPayment: any,
        onCancel: any,
        onFailed: any
      ) =>
      async (createPaymentEvent: CreatePaymentMethodResultEvent) => {
        setPaymentMethod(createPaymentEvent.content)
        const paymentCompletedParams: PaymentCompletedParams = {
          paymentMethodId: null,
          surchargeAmount: null
        }

        const { firstName, lastName, phoneNumber, email, phoneCountryCode } =
          getCheckoutInfo(
            checkoutFormData,
            createPaymentEvent.content.billingDetails
          )

        if (
          !useGiftCardFlow &&
          (!firstName || !lastName || !phoneNumber || !email)
        ) {
          // Couldn't get all the info from the checkout form or the digital wallet, so prompt the user to input it
          setUserInfoRequired(true)
          onCancel(true, firstName, lastName, phoneNumber, email)
        } else {
          try {
            let isValid = true
            if (useGiftCardFlow) {
              await updateGiftCardPaymentIntent(
                checkoutFormData.email,
                cart?.order?.discounts?.globalReward?.amount || 0
              )
            } else {
              if (validate) {
                isValid = await validate()
              }

              if (isValid && serverSpiEnabled && paymentIntent) {
                const paymentMethodId =
                  createPaymentEvent.content.paymentMethodId
                const paymentIntentId = paymentIntent.id
                const surchargeAmount =
                  surcharges && PaymentType.Credit in surcharges
                    ? surcharges[PaymentType.Credit]?.surchargeAmount
                    : undefined

                // Server-side SPI no longer requires updatePaymentIntent or confirmPayment, so jump straight to placing the order
                await onConfirmPayment(
                  paymentIntentId,
                  firstName,
                  lastName,
                  phoneNumber,
                  email,
                  phoneCountryCode,
                  {
                    paymentMethodId,
                    surchargeAmount,
                    sessionSecret: paymentIntent.sessionSecret
                  }
                )
              } else if (isValid) {
                // email will never be undefined because of the condition above, but TS isn't smart enough to figure that out
                const updatedIntent = await updatePaymentIntent(
                  email || '',
                  giftCardAppliedAmount || 0,
                  cart?.order?.discounts?.globalReward?.amount || 0,
                  toastCashAppliedAmount || 0,
                  spiSurchargingEnabled
                    ? createPaymentEvent.content.paymentMethodId
                    : undefined
                )

                if (spiSurchargingEnabled) {
                  paymentCompletedParams.paymentMethodId =
                    createPaymentEvent.content.paymentMethodId
                  paymentCompletedParams.surchargeAmount =
                    updatedIntent?.surchargeAmount
                }
              }
            }

            if (!isValid) {
              // the validate function should set order errors, so don't pass back an error in this callback
              onFailed()
            } else if (!serverSpiEnabled || useGiftCardFlow) {
              // without server-side SPI, and for the gift card flow either way,
              // the payment is confirmed through the SPI SDK
              trackSpi('temp_SPI_confirming_payment', {})
              await spiSdk.confirmPayment(async (ev: any) => {
                trackSpi('temp_SPI_confirmPayment_success', {})
                setConfirmedPayment(ev.content.payment)
                await onConfirmPayment(
                  ev.content.payment.externalReferenceId,
                  firstName,
                  lastName,
                  phoneNumber,
                  email,
                  phoneCountryCode,
                  paymentCompletedParams
                )
              }, spiConfirmPaymentFailureCallback(onFailed))
            }
          } catch (e) {
            trackSpi('temp_SPI_updateOrConfirmPayment_failed', {
              message: e.message,
              paymentIntentId: paymentIntent?.id
            })
            reportErrorMessageWithData(
              'Error placing order - failed to update payment intent',
              { message: e.message, paymentIntentId: paymentIntent?.id }
            )
            onFailed({
              type: PlaceOrderErrorCode.PlaceOrderFailed,
              message: 'We were unable to place your order.'
            })
            spiSdk.clearPaymentSelection()
          }
        }
      },
    [
      trackSpi,
      spiSdk,
      serverSpiEnabled,
      updatePaymentIntent,
      cart?.order?.discounts?.globalReward?.amount,
      spiConfirmPaymentFailureCallback,
      spiSurchargingEnabled,
      surcharges,
      updateGiftCardPaymentIntent,
      useGiftCardFlow,
      paymentIntent
    ]
  )

  const spiPaymentMethodFailureCallback = useCallback(
    (onFailed: any, onCancel?: any) => (ev: any) => {
      setPaymentMethod(undefined)
      // ignore errors when the user cancelled the digital wallet payment (i.e. they closed the modal)
      if (
        ev?.cause?.message === 'User cancelled Google Pay flow.' ||
        ev?.cause?.message === 'Apple Pay session cancelled.'
      ) {
        onCancel?.(false)
      } else {
        trackSpi('temp_SPI_createPaymentMethod_failed', {
          message: ev?.cause?.message || ev?.message || ev
        })
        reportErrorMessageWithData(
          'Error placing order - failed to create payment method',
          { message: ev?.cause?.message || ev?.message, error: ev }
        )
        onFailed({
          type: PlaceOrderErrorCode.PlaceOrderFailed,
          message: 'We were unable to place your order.'
        })
        spiSdk.clearPaymentSelection()
      }
    },
    [trackSpi, spiSdk]
  )

  const createPaymentMethod = useCallback(
    async (
      giftCardAppliedAmount: number,
      toastCashAppliedAmount: number,
      onSuccess: (ev: CreatePaymentMethodResultEvent) => void | Promise<void>,
      onFailure: any,
      onCancel?: any
    ) => {
      const config = getConfig(
        tipAmount,
        fundraisingAmount,
        giftCardAppliedAmount,
        toastCashAppliedAmount
      )
      const lineItems = config.lineItems // line items to display on the digital wallet receipt
      const surchargeAmount =
        surcharges && PaymentType.Credit in surcharges
          ? surcharges[PaymentType.Credit]?.surchargeAmount
          : undefined
      await spiSdk.createPaymentMethod(
        onSuccess,
        spiPaymentMethodFailureCallback(onFailure, onCancel),
        lineItems,
        surchargeAmount
      )
    },
    [
      fundraisingAmount,
      getConfig,
      spiSdk,
      surcharges,
      tipAmount,
      spiPaymentMethodFailureCallback
    ]
  )

  useEffect(() => {
    if (paymentMethod?.card) setCardDetails(paymentMethod.card)
  }, [paymentMethod])

  const completePayment = useCallback<PaymentContextType['completePayment']>(
    async (
      checkoutFormData,
      giftCardAppliedAmount,
      toastCashAppliedAmount,
      validate,
      onComplete,
      onCancel,
      onFailed
    ) => {
      // When using a payment from the SPI SDK, a payment method must be created and confirmed via the SDK
      if (paymentOption === PaymentOption.PayNow) {
        if (spiSdk) {
          trackSpi('temp_SPI_completing_payment', { paymentType })
          if (paymentMethod) {
            // the payment method has already been created, so we don't need to create it again
            spiPaymentMethodSuccessCallback(
              checkoutFormData,
              giftCardAppliedAmount,
              toastCashAppliedAmount,
              validate,
              onComplete,
              onCancel,
              onFailed
            )({
              content: {
                paymentMethodType: paymentMethod.paymentMethodType,
                billingDetails: paymentMethod.billingDetails,
                paymentMethodId: paymentMethod.paymentMethodId
              }
            })
          } else {
            await createPaymentMethod(
              giftCardAppliedAmount,
              toastCashAppliedAmount,
              spiPaymentMethodSuccessCallback(
                checkoutFormData,
                giftCardAppliedAmount,
                toastCashAppliedAmount,
                validate,
                onComplete,
                onCancel,
                onFailed
              ),
              onFailed,
              onCancel
            )
          }
        } else {
          trackSpi('temp_SPI_completing_payment_error_no_sdk', {})
          reportErrorMessageWithData('Error placing order - SPI SDK not loaded')
          onFailed({
            type: PlaceOrderErrorCode.PlaceOrderFailed,
            message: 'We were unable to place your order.'
          })
        }
      } else {
        try {
          await onComplete()
        } catch (e) {
          onFailed(e)
        }
      }
    },
    [
      paymentOption,
      spiSdk,
      trackSpi,
      paymentType,
      paymentMethod,
      spiPaymentMethodSuccessCallback,
      createPaymentMethod
    ]
  )

  const completeGiftCardPayment = useCallback(
    async (
      { email }: { email: string },
      onComplete: any,
      onCancel: any,
      onFailed: any
    ) => {
      // When using a payment from the SPI SDK, a payment method must be created and confirmed via the SDK
      if (spiSdk) {
        await spiSdk.createPaymentMethod(
          spiPaymentMethodSuccessCallback(
            { email },
            null,
            null,
            null,
            onComplete,
            onCancel,
            onFailed
          ),
          spiPaymentMethodFailureCallback(onFailed, onCancel),
          [
            {
              label: 'Gift card',
              amount: giftCardAmount,
              id: 'total-order-amount'
            }
          ] // line items to display on the digital wallet receipt
        )
      } else {
        reportErrorMessageWithData('Error placing order - SPI SDK not loaded')
        trackSpi('temp_SPI_sdk_failed', {})
        onFailed({
          type: PlaceOrderErrorCode.PlaceOrderFailed,
          message: 'We were unable to place your order.'
        })
      }
    },
    [
      spiSdk,
      spiPaymentMethodSuccessCallback,
      spiPaymentMethodFailureCallback,
      giftCardAmount,
      trackSpi
    ]
  )

  return (
    <PaymentContext.Provider
      value={{
        // SPI SDK fields
        createPaymentIntent,
        paymentIntent,
        paymentIntentFailure,
        retryPaymentIntent,
        createPaymentMethod,
        setPaymentMethod,
        completePayment,
        completeGiftCardPayment,
        confirmedPayment,
        billingDetails: paymentMethod?.billingDetails,
        userInfoRequired,
        setUserInfoRequired,
        isPaymentValid,
        setIsPaymentValid,
        paymentType,
        setPaymentType: updatePaymentType,
        // Old CC form fields - should be able to clean up or remove eventually
        paymentOption,
        setPaymentOption,
        newCreditCard,
        setNewCreditCard,
        setSaveNewCreditCard: (saveCard: boolean) =>
          setNewCreditCard((old) => (old ? { ...old, saveCard } : null)),
        selectedCreditCard,
        setSelectedCreditCard,
        // Common fields
        tipEnabled,
        tipAmount,
        setTipAmount,
        tipPercent,
        setTipPercent,
        fundraisingEnabled,
        fundraisingAmount,
        setFundraisingAmount,
        toastCashInfo,
        setToastCashInfo,
        cardDetails,
        giftCardPaymentIntentError
      }}
    >
      {props.children}
    </PaymentContext.Provider>
  )
}

export const usePayment = () => {
  const context = useContext(PaymentContext)
  if (!context) {
    throw new Error('usePayment must be used within a PaymentContextProvider')
  }

  return context
}

export const useOptionalPayment = () => {
  const context = useContext(PaymentContext)
  return context
}
